/* GrowthOS — "Create New Quote" modal (P&C flow starter).
   Reskin of the legacy react-bootstrap `quoteai_popup` modal used by both the
   Quotes list (dashboard.jsx) and the P&C dashboard (quoteAiDashboard.jsx).
   Reuses the shared `gos-import-*` modal shell (overlay/head/foot) + a small
   `gos-qc-*` block for the line-of-business card grid. Behavior preserved exactly:
   pick a line of business + primary state, answer "have an ACORD form?" — on "No"
   → Start Quote navigates to the insured-information step; "Yes" reveals an upload
   field (the legacy flow left the Yes branch unhandled, kept as-is). */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const PATHS = {
  quote: 'M14 3v5h5M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-5ZM8 13h8M8 17h5',
  x: 'M18 6 6 18M6 6l12 12',
  info: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20ZM12 16v-4M12 8h.01',
  upload: 'M12 16V4M7 9l5-5 5 5M5 20h14',
  check: 'M20 6 9 17l-5-5',
  // line-of-business glyphs
  comp: 'M12 2 4 5v6c0 5 3.5 8 8 11 4.5-3 8-6 8-11V5l-8-3Z',
  liability: 'M12 3v18M7 21h10M6 8h12M6 8l-3 6a3 3 0 0 0 6 0L6 8Zm12 0-3 6a3 3 0 0 0 6 0l-3-6Z',
  auto: 'M5 13l1.5-4.5A2 2 0 0 1 8.4 7h7.2a2 2 0 0 1 1.9 1.5L19 13M5 13h14v4H5v-4Zm2 4v2M17 17v2M7.5 15h.01M16.5 15h.01',
  property: 'M3 11l9-7 9 7M5 10v9h14v-9M10 19v-5h4v5',
  package: 'M12 3 3 7.5V16l9 4.5 9-4.5V7.5L12 3Zm0 0v18M3 7.5l9 4.5 9-4.5',
  other: 'M4 6h16M4 12h16M4 18h16',
};

const Ic = ({ name, size = 16, className }) => (
  <svg className={className} viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <path d={PATHS[name]} />
  </svg>
);

const BUSINESS_LINES = [
  { id: 1, glyph: 'comp', label: 'Worker Compensation', tone: 'blue' },
  { id: 2, glyph: 'liability', label: 'General Liability', tone: 'purple' },
  { id: 3, glyph: 'auto', label: 'Auto', tone: 'amber' },
  { id: 4, glyph: 'property', label: 'Property', tone: 'green' },
  { id: 5, glyph: 'package', label: 'Package', tone: 'rose' },
  { id: 6, glyph: 'other', label: 'Other', tone: 'gray' },
];

const STATES = ['California', 'Texas', 'New York'];

const QuoteCreateModal = ({ onClose }) => {
  const navigate = useNavigate();
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [selectedState, setSelectedState] = useState('');
  const [acordForm, setAcordForm] = useState('');
  const [file, setFile] = useState(null);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handleStartQuote = () => {
    if (acordForm === 'no') {
      navigate('/quoteAI/insuredInformationPage');
    }
    // "Yes" branch: legacy flow left unhandled (ACORD upload path); kept as-is.
  };

  return (
    <div
      className="gos-import-overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="gos-import-modal gos-qc-modal" role="dialog" aria-modal="true" aria-label="Create new quote">
        <div className="gos-import-head">
          <div className="gos-import-head-l">
            <span className="gos-import-step-ic"><Ic name="quote" size={16} /></span>
            <div>
              <div className="gos-import-title">Create New Quote</div>
              <div className="gos-import-sub">Select the line of business and primary state to start a new quote.</div>
            </div>
          </div>
          <button type="button" className="gos-import-close" onClick={onClose} aria-label="Close">
            <Ic name="x" size={18} />
          </button>
        </div>

        <div className="gos-import-body">
          <div className="gos-qc-section">
            <div className="gos-qc-label">Line of Business</div>
            <div className="gos-qc-grid">
              {BUSINESS_LINES.map((b) => (
                <button
                  key={b.id}
                  type="button"
                  className={'gos-qc-card' + (selectedBusiness === b.id ? ' on' : '')}
                  onClick={() => setSelectedBusiness(b.id)}
                >
                  <span
                    className="gos-qc-card-ic"
                    style={{ background: `var(--gos-${b.tone}-bg)`, color: `var(--gos-${b.tone}-fg)` }}
                  >
                    <Ic name={b.glyph} size={20} />
                  </span>
                  <span className="gos-qc-card-label">{b.label}</span>
                  {selectedBusiness === b.id ? (
                    <span className="gos-qc-card-check"><Ic name="check" size={12} /></span>
                  ) : null}
                </button>
              ))}
            </div>
          </div>

          <div className="gos-qc-section">
            <div className="gos-qc-label">
              Primary State
              <span className="gos-qc-info" title="Select the primary state for the quote."><Ic name="info" size={13} /></span>
            </div>
            <select
              className="gos-qc-select"
              value={selectedState}
              onChange={(e) => setSelectedState(e.target.value)}
            >
              <option value="">Select State</option>
              {STATES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>

          <div className="gos-qc-section">
            <div className="gos-qc-label">Do you have an ACORD form?</div>
            <div className="gos-qc-radios">
              <label className={'gos-qc-radio' + (acordForm === 'yes' ? ' on' : '')}>
                <input type="radio" name="acordForm" checked={acordForm === 'yes'} onChange={() => setAcordForm('yes')} />
                <span className="gos-qc-radio-dot" />
                Yes
              </label>
              <label className={'gos-qc-radio' + (acordForm === 'no' ? ' on' : '')}>
                <input type="radio" name="acordForm" checked={acordForm === 'no'} onChange={() => setAcordForm('no')} />
                <span className="gos-qc-radio-dot" />
                No
              </label>
            </div>
            {acordForm === 'yes' && (
              <label className="gos-qc-upload">
                <input type="file" style={{ display: 'none' }} onChange={(e) => setFile(e.target.files && e.target.files[0])} />
                <Ic name="upload" size={16} />
                <span>{file ? file.name : 'Upload ACORD form'}</span>
              </label>
            )}
          </div>
        </div>

        <div className="gos-import-foot">
          <button type="button" className="gos-btn gos-btn-ghost" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="gos-btn gos-btn-primary"
            onClick={handleStartQuote}
            disabled={acordForm !== 'no'}
          >
            Start Quote
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuoteCreateModal;
